import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  canRegisterOpeningBalance,
  computeProjectedBalance,
  euroToCents,
  todayMadridDateString,
  TreasuryBalanceBucketInput,
  TreasuryOpeningBalanceSnapshot,
} from "@/finance/financeTreasuryBalance";
import { canAccessFinanceRoute } from "@/finance/financeNav";
import { trpc } from "@/lib/trpc";
import {
  FinancePageHeader,
  FinanceMetricCard,
  FinanceMetricGrid,
  FinanceSection,
  FinanceLoadingState,
  FinanceErrorState,
  FinanceAccessDeniedState,
} from "@/components/finance/ui";
import {
  AlertCircle,
  ArrowDownRight,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  PiggyBank,
  Plus,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import React, { useMemo, useState } from "react";

type TreasuryHorizon = "13w" | "6m" | "18m";

const euro = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });

const horizons: Array<{ value: TreasuryHorizon; label: string }> = [
  { value: "13w", label: "13 semanas" },
  { value: "6m", label: "6 meses" },
  { value: "18m", label: "18 meses" },
];

const scenarios = [
  "Base",
  "Optimista",
  "Conservador",
];

function formatCents(cents: number | null | undefined) {
  if (cents === null || cents === undefined) return "—";
  return euro.format(cents / 100);
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("es-ES");
}

export default function FinanceTreasuryView() {
  // 1. All hooks MUST be declared unconditionally at top level
  const { user } = useAuth();
  const allowed = user ? canAccessFinanceRoute("/accounting/treasury", user.role) : false;
  const [horizon, setHorizon] = useState<TreasuryHorizon>("13w");
  const [expandedWeek, setExpandedWeek] = useState<string | null>(null);
  const forecastQuery = trpc.finance.treasury.forecast.useQuery({ horizon }, { enabled: allowed });
  const utils = trpc.useUtils();

  const canRegisterOpening = canRegisterOpeningBalance(user?.role);
  const openingSnapshotsQuery = trpc.finance.treasury.openingBalance.list.useQuery(undefined, { enabled: allowed });
  type SnapshotRow = NonNullable<typeof openingSnapshotsQuery.data>[number];
  const openingSnapshots: TreasuryOpeningBalanceSnapshot[] = (openingSnapshotsQuery.data ?? []).map((row: SnapshotRow) => ({
    id: String(row.id),
    date: row.effectiveDate,
    amountCents: row.amountCents,
    notes: row.notes ?? null,
    registeredBy: String(row.createdById),
    registeredByName: row.createdByName ?? "",
    registeredAt: row.createdAt instanceof Date ? row.createdAt.toISOString() : String(row.createdAt),
  }));
  const [showOpeningForm, setShowOpeningForm] = useState(false);
  const [openingDate, setOpeningDate] = useState<string>(todayMadridDateString());
  const [openingAmount, setOpeningAmount] = useState<string>("");
  const [openingNotes, setOpeningNotes] = useState<string>("");

  const forecast = forecastQuery.data;

  const projection = useMemo(() => {
    const buckets: TreasuryBalanceBucketInput[] = (forecast?.buckets ?? []).map((bucket) => ({
      key: bucket.key,
      label: bucket.label,
      startsOn: bucket.startsOn,
      endsOn: bucket.endsOn,
      collectionsCents: bucket.collectionsCents,
      paymentsCents: bucket.paymentsCents,
    }));
    return computeProjectedBalance(openingSnapshots, buckets);
  }, [forecast?.buckets, openingSnapshots]);

  const maxFlow = useMemo(() => {
    return Math.max(
      1,
      ...(forecast?.buckets ?? []).map((bucket) => Math.max(bucket.collectionsCents, bucket.paymentsCents))
    );
  }, [forecast?.buckets]);

  const totals = useMemo(() => {
    const totalCollections = (forecast?.buckets ?? []).reduce((total, bucket) => total + bucket.collectionsCents, 0);
    const totalPayments = (forecast?.buckets ?? []).reduce((total, bucket) => total + bucket.paymentsCents, 0);
    const totalNet = totalCollections - totalPayments;
    return { totalCollections, totalPayments, totalNet };
  }, [forecast?.buckets]);

  const registerOpeningBalance = trpc.finance.treasury.openingBalance.register.useMutation({
    onSuccess: () => {
      utils.finance.treasury.openingBalance.list.invalidate();
      setShowOpeningForm(false);
      setOpeningAmount("");
      setOpeningNotes("");
      toast.success("Saldo inicial registrado.");
    },
    onError: (err) => toast.error(`Error al registrar saldo: ${err.message}`),
  });

  // 2. Early return AFTER all hooks have been declared
  if (!allowed) {
    return (
      <FinanceAccessDeniedState
        title="Acceso no disponible"
        message="Tesorería todavía no está disponible para tu perfil de usuario."
      />
    );
  }

  function handleRegisterOpeningBalance(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const amountCents = euroToCents(openingAmount);
    const effectiveDate = openingDate || todayMadridDateString();
    registerOpeningBalance.mutate({
      effectiveDate,
      amountCents,
      notes: openingNotes.trim() ? openingNotes.trim() : null,
    });
  }

  const horizonSelector = (
    <div className="flex flex-wrap gap-1.5 bg-muted/40 p-1 rounded-lg border border-border/50">
      {horizons.map((item) => (
        <Button
          key={item.value}
          type="button"
          size="sm"
          variant={horizon === item.value ? "default" : "ghost"}
          className="h-7 text-xs px-2.5 font-medium"
          onClick={() => setHorizon(item.value)}
        >
          {item.label}
        </Button>
      ))}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <FinancePageHeader
          eyebrow="Finanzas y Contabilidad"
          title="Tesorería"
          description="Previsión operativa de cobros y pagos con fuentes reales actuales. El saldo proyectado se mantiene oculto hasta registrar un saldo inicial real."
          updatedAt={forecast?.updatedAt}
          secondaryActions={horizonSelector}
          note={
            forecast?.coverage?.partial
              ? {
                  title: "Nota de cobertura de tesorería",
                  message: forecast.coverage.note,
                  missingSources: forecast.coverage.missingSources,
                  type: "amber",
                }
              : undefined
          }
        />

        {forecastQuery.isLoading ? (
          <FinanceLoadingState cardsCount={3} />
        ) : forecastQuery.isError ? (
          <FinanceErrorState
            title="Error al cargar tesorería"
            message={forecastQuery.error.message}
            onRetry={() => forecastQuery.refetch()}
          />
        ) : (
          <>
            {forecast?.forecastExclusions?.note ? (
              <Card className="min-w-0">
                <CardContent className="flex items-start gap-3 rounded-lg border border-amber-200/80 bg-amber-50/50 px-4 py-3 text-xs text-amber-950 shadow-2xs">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <div className="flex-1 space-y-1.5">
                    <span className="font-medium">{forecast.forecastExclusions.note}</span>
                    {forecast.forecastExclusions?.items && forecast.forecastExclusions.items.length > 0 && (
                      <div className="mt-1 space-y-1 border-t border-amber-200/60 pt-2">
                        {forecast.forecastExclusions.items.map((item: any, i: number) => (
                          <div key={i} className="flex items-center justify-between gap-2 text-[11px]">
                            <span className="truncate">{item.conceptLabel}</span>
                            <span className="tabular-nums font-semibold">{euro.format(item.amountCents / 100)}</span>
                            <span className="text-muted-foreground ml-2 shrink-0">Sin fecha de pago</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : null}

            {/* Max 4 primary KPI cards in desktop first row */}
            <FinanceMetricGrid maxColumns={3}>
              <FinanceMetricCard
                title="Cobros previstos"
                amountCents={totals.totalCollections}
                variant="emerald"
                subtitle="Previsión de entradas de caja"
              />
              <FinanceMetricCard
                title="Pagos previstos"
                amountCents={totals.totalPayments}
                variant="rose"
                subtitle="Previsión de salidas de caja"
              />
              <FinanceMetricCard
                title="Flujo neto"
                amountCents={totals.totalNet}
                variant={totals.totalNet >= 0 ? "emerald" : "rose"}
                subtitle="Diferencia cobros vs pagos"
              />
            </FinanceMetricGrid>

            {/* Opening balance section */}
            <FinanceSection
              title="Saldo inicial y proyeccion"
              description="El saldo proyectado combina el saldo inicial registrado con los cobros y pagos previstos. Modelo aditivo: no se borran snapshots."
              icon={PiggyBank}
              action={canRegisterOpening && (
                  <Button
                    type="button"
                    size="sm"
                    variant={showOpeningForm ? "outline" : "default"}
                    onClick={() => setShowOpeningForm((previous) => !previous)}
                    aria-expanded={showOpeningForm}
                    aria-controls="treasury-opening-balance-form"
                    className="gap-1 text-xs"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>Registrar saldo inicial</span>
                  </Button>
                )}
            >
              <div className="space-y-4">
                {canRegisterOpening && showOpeningForm && (
                  <form
                    id="treasury-opening-balance-form"
                    className="space-y-3 rounded-lg border bg-muted/30 p-4 shadow-2xs"
                    onSubmit={handleRegisterOpeningBalance}
                  >
                    <div className="grid gap-3 md:grid-cols-3">
                      <div className="space-y-1">
                        <label htmlFor="treasury-opening-date" className="text-xs font-medium text-muted-foreground">
                          Fecha (ISO, Europa/Madrid)
                        </label>
                        <Input
                          id="treasury-opening-date"
                          type="date"
                          value={openingDate}
                          onChange={(event) => setOpeningDate(event.target.value)}
                          required
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="treasury-opening-amount" className="text-xs font-medium text-muted-foreground">
                          Importe (EUR)
                        </label>
                        <Input
                          id="treasury-opening-amount"
                          type="text"
                          inputMode="decimal"
                          placeholder="0,00"
                          value={openingAmount}
                          onChange={(event) => setOpeningAmount(event.target.value)}
                          required
                          className="h-8 text-xs"
                        />
                        <p className="text-[11px] text-muted-foreground">
                          Se muestra en euros y se almacena en centimos.
                        </p>
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="treasury-opening-notes" className="text-xs font-medium text-muted-foreground">
                          Notas (opcional)
                        </label>
                        <Textarea
                          id="treasury-opening-notes"
                          rows={1}
                          value={openingNotes}
                          onChange={(event) => setOpeningNotes(event.target.value)}
                          className="min-h-8 text-xs resize-none"
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Button type="submit" size="sm" className="h-8 text-xs font-medium" disabled={registerOpeningBalance.isPending}>
                        {registerOpeningBalance.isPending ? "Guardando..." : "Guardar saldo inicial"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setShowOpeningForm(false)}
                        className="h-8 text-xs"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </form>
                )}

                {projection.hasOpeningBalance ? (
                  <div className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                      <div className="rounded-md border p-3 bg-muted/20">
                        <p className="text-xs text-muted-foreground">Saldo inicial ({projection.openingDate})</p>
                        <p className="mt-1 text-base font-semibold tabular-nums">{formatCents(projection.openingBalanceCents)}</p>
                      </div>
                      <div className="rounded-md border p-3 bg-emerald-50/20 border-emerald-200/60">
                        <p className="text-xs text-muted-foreground">Cobros esperados</p>
                        <p className="mt-1 text-base font-semibold text-emerald-700 tabular-nums">
                          {formatCents(projection.cumulativeCollectionsCents)}
                        </p>
                      </div>
                      <div className="rounded-md border p-3 bg-rose-50/20 border-rose-200/60">
                        <p className="text-xs text-muted-foreground">Pagos esperados</p>
                        <p className="mt-1 text-base font-semibold text-rose-700 tabular-nums">
                          {formatCents(projection.cumulativePaymentsCents)}
                        </p>
                      </div>
                      <div className="rounded-md border p-3 bg-primary/5 border-primary/20">
                        <p className="text-xs text-muted-foreground">Saldo proyectado final</p>
                        <p className="mt-1 text-base font-bold tabular-nums">{formatCents(projection.projectedBalanceCents)}</p>
                      </div>
                    </div>
                    <div className="rounded-md border p-2.5 text-xs text-muted-foreground bg-muted/30">
                      Flujo neto acumulado: <span className="font-semibold text-foreground tabular-nums">{formatCents(projection.cumulativeNetFlowCents)}</span>.
                      Audit trail: {openingSnapshots.length} snapshot(s) registrado(s).
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-4 text-xs text-muted-foreground bg-muted/10 leading-relaxed">
                    No hay ningun saldo inicial registrado. Registra un saldo inicial para ver el saldo proyectado.
                    {canRegisterOpening
                      ? " Usa el botón Registrar saldo inicial."
                      : " Solo los roles admin y admin_manager pueden registrarlo."}
                  </div>
                )}
              </div>
            </FinanceSection>

            {/* Cash flow forecast and scenarios */}
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(300px,0.8fr)]">
              <FinanceSection
                title="Flujo de caja"
                description={`Cobros y pagos previstos. Actualizado ${formatDateTime(forecast?.updatedAt)}.`}
                badge={<Badge variant="outline" className="text-xs font-normal">{horizons.find((item) => item.value === horizon)?.label}</Badge>}
              >
                <div className="space-y-3">
                  {forecast?.buckets.map((bucket) => (
                    <div key={bucket.key} className="rounded-lg border border-border/80 p-3 shadow-2xs hover:border-border transition-all">
                      <div className="grid gap-2 md:grid-cols-[120px_minmax(0,1fr)_130px] md:items-center">
                        <div>
                          <p className="text-xs font-semibold">{bucket.label}</p>
                          <p className="text-[11px] text-muted-foreground">{bucket.startsOn} - {bucket.endsOn}</p>
                        </div>
                        <div className="space-y-1.5">
                          <div className="h-2 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full bg-emerald-500 rounded-full"
                              style={{ width: `${Math.max(2, (bucket.collectionsCents / maxFlow) * 100)}%` }}
                            />
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full bg-rose-500 rounded-full"
                              style={{ width: `${Math.max(2, (bucket.paymentsCents / maxFlow) * 100)}%` }}
                            />
                          </div>
                        </div>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center justify-between gap-2 text-emerald-700 dark:text-emerald-400 font-medium">
                            <span className="flex items-center gap-0.5"><ArrowUpRight className="h-3 w-3" />Cobros</span>
                            <span className="tabular-nums">{formatCents(bucket.collectionsCents)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2 text-rose-700 dark:text-rose-400 font-medium">
                            <span className="flex items-center gap-0.5"><ArrowDownRight className="h-3 w-3" />Pagos</span>
                            <span className="tabular-nums">{formatCents(bucket.paymentsCents)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2 font-semibold pt-0.5 border-t border-border/40">
                            <span>Neto</span>
                            <span className="tabular-nums">{formatCents(bucket.netCents)}</span>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="mt-2 flex w-full items-center justify-center gap-1 border-t border-border/40 pt-2 text-[11px] text-muted-foreground hover:text-foreground focus:outline-none"
                        onClick={() => setExpandedWeek(expandedWeek === bucket.key ? null : bucket.key)}
                        aria-expanded={expandedWeek === bucket.key}
                        aria-label={`Detalle ${bucket.label}`}
                      >
                        {expandedWeek === bucket.key ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )}
                        <span>{expandedWeek === bucket.key ? "Ocultar detalle" : "Ver detalle"}</span>
                      </button>

                      {expandedWeek === bucket.key && (
                        bucket.items && bucket.items.length > 0 ? (
                          <div className="mt-2 space-y-1 pt-1 border-t border-border/30">
                            {bucket.items.map((item, i) => (
                              <div
                                key={i}
                                className={`flex items-center justify-between gap-2 text-[11px] py-0.5 ${item.isProvisional ? "text-amber-700/90" : "text-muted-foreground"}`}
                              >
                                <span
                                  className={`font-semibold tabular-nums ${
                                    item.type === "collection"
                                      ? "text-emerald-700 dark:text-emerald-400"
                                      : item.isProvisional
                                      ? "text-amber-600"
                                      : "text-rose-700 dark:text-rose-400"
                                  }`}
                                >
                                  {item.type === "collection" ? "+" : "-"}{euro.format(item.amountCents / 100)}
                                  {item.isProvisional && (
                                    <span className="ml-1 rounded bg-amber-100 px-1 py-px text-[9px] font-medium text-amber-800">
                                      provis.
                                    </span>
                                  )}
                                </span>
                                <span className="mx-2 flex-1 truncate" title={item.conceptLabel}>{item.conceptLabel}</span>
                                <span className="text-[10px] text-muted-foreground shrink-0">{item.dateUsed}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-1 text-[11px] text-muted-foreground text-center py-1">Sin detalle disponible para este período.</p>
                        )
                      )}
                    </div>
                  ))}
                </div>
              </FinanceSection>

              <FinanceSection
                title="Escenarios"
                description="Diseño futuro. No modifica datos reales ni cambia previsiones guardadas."
                icon={TrendingUp}
              >
                <div className="space-y-2.5">
                  {scenarios.map((scenario) => (
                    <div key={scenario} className="rounded-md border border-border/70 p-3 bg-muted/10">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-foreground">{scenario}</p>
                        <Badge variant="outline" className="text-[10px] h-4">Futuro</Badge>
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground leading-relaxed">
                        Pendiente de reglas de sensibilidad y permisos. Sin escritura en esta fase.
                      </p>
                    </div>
                  ))}
                </div>
              </FinanceSection>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
